/**
 * Shared Graph Utilities
 * Common functions for graph/chart functionality used across admin and public views
 * Optimized for performance, maintainability, and stability
 */

(function (window) {
  'use strict';

  // Constants
  const DEFAULT_COLORS = [
    "#FF6384", "#36A2EB", "#FFCE56", "#4BC0C0", "#9966FF",
    "#FF9F40", "#FF6384", "#C9CBCF", "#4BC0C0", "#FF6384"
  ];

  const CHART_TYPE_MAP = {
    'column': 'bar',
    'stacked-column': 'bar',
    'stacked-100-column': 'bar',
    'bar': 'bar',
    'stacked-bar': 'bar',
    'stacked-100-bar': 'bar',
    'line': 'line',
    'line-column': 'line',
    'area': 'line',
    'stacked-area': 'line',
    'stacked-100-area': 'line',
    'bubble': 'bubble',
    'pie': 'pie',
    'wordcloud': 'wordCloud'
  };

  const HORIZONTAL_BAR_TYPES = ['bar', 'stacked-bar', 'stacked-100-bar'];
  const AREA_TYPES = ['area', 'stacked-area', 'stacked-100-area'];
  const STACKED_TYPES = ['stacked-column', 'stacked-bar', 'stacked-area', 'stacked-100-column', 'stacked-100-bar', 'stacked-100-area'];
  const STACKED_100_TYPES = ['stacked-100-column', 'stacked-100-bar', 'stacked-100-area'];

  /**
   * Graph Utilities Namespace
   */
  const GraphUtils = {
    /**
     * Validates raw data structure
     * @param {Array} data - Raw data array
     * @param {string} chartType - Chart type
     * @returns {Object} {valid: boolean, error: string|null}
     */
    validateData: function (data, chartType) {
      if (!data || !Array.isArray(data)) {
        return { valid: false, error: 'Data must be an array' };
      }

      if (data.length === 0) {
        return { valid: false, error: 'Data cannot be empty' };
      }

      if (!data[0] || !Array.isArray(data[0])) {
        return { valid: false, error: 'Data rows must be arrays' };
      }

      const numCols = data[0].length;
      if (numCols < 2) {
        return { valid: false, error: 'Data must have at least 2 columns' };
      }

      // Chart-specific validation
      if ((chartType === 'pie' || chartType === 'wordcloud') && numCols < 2) {
        return { valid: false, error: 'Pie and wordcloud charts require at least 2 columns' };
      }

      if (chartType === 'bubble' && numCols < 3) {
        return { valid: false, error: 'Bubble charts require at least 3 columns (Label | X | Y)' };
      }

      return { valid: true, error: null };
    },

    /**
     * Safely parses a numeric value
     * @param {*} value - Value to parse
     * @param {number} defaultValue - Default value if parsing fails
     * @returns {number}
     */
    parseNumber: function (value, defaultValue) {
      if (value === undefined || value === null || value === '') {
        return defaultValue;
      }
      const parsed = parseFloat(value);
      return isNaN(parsed) ? defaultValue : parsed;
    },

    /**
     * Gets default color for a dataset index
     * @param {number} index - Dataset index
     * @returns {string}
     */
    getDefaultColor: function (index) {
      return DEFAULT_COLORS[index % DEFAULT_COLORS.length];
    },

    /**
     * Determines Chart.js type from chart type string
     * @param {string} chartType - Chart type
     * @returns {string} Chart.js type
     */
    getChartJsType: function (chartType) {
      return CHART_TYPE_MAP[chartType] || 'bar';
    },

    /**
     * Checks if chart type is horizontal bar
     * @param {string} chartType - Chart type
     * @returns {boolean}
     */
    isHorizontalBar: function (chartType) {
      return HORIZONTAL_BAR_TYPES.includes(chartType);
    },

    /**
     * Checks if chart type is area
     * @param {string} chartType - Chart type
     * @returns {boolean}
     */
    isArea: function (chartType) {
      return AREA_TYPES.includes(chartType);
    },

    /**
     * Checks if chart type is stacked
     * @param {string} chartType - Chart type
     * @returns {boolean}
     */
    isStacked: function (chartType) {
      return STACKED_TYPES.includes(chartType);
    },

    /**
     * Checks if chart type is 100% stacked
     * @param {string} chartType - Chart type
     * @returns {boolean}
     */
    isStacked100: function (chartType) {
      return STACKED_100_TYPES.includes(chartType);
    },

    /**
     * Calculates aspect ratio value from string or number
     * @param {string|number} aspectRatio - Aspect ratio (e.g., "16:9", "1.77", or number)
     * @returns {number|null}
     */
    calculateAspectRatio: function (aspectRatio) {
      if (!aspectRatio || aspectRatio === 'auto' || aspectRatio === '') {
        return null;
      }

      // If it's already a number, return it
      const numericValue = parseFloat(aspectRatio);
      if (!isNaN(numericValue) && numericValue > 0) {
        return numericValue;
      }

      // Try to parse as ratio string (e.g., "16:9")
      const parts = String(aspectRatio).split(':');
      if (parts.length === 2) {
        const width = parseFloat(parts[0]);
        const height = parseFloat(parts[1]);

        if (!isNaN(width) && !isNaN(height) && height !== 0) {
          return width / height;
        }
      }

      return null;
    },

    /**
     * Transforms raw data to Chart.js format
     * @param {string} chartType - Chart type
     * @param {Array} data - Raw data array
     * @param {boolean} firstRowIsHeader - Whether first row is header
     * @param {Object} columnTypes - Column type mapping
     * @param {Object} colors - Color mapping
     * @returns {Object} {labels: Array, datasets: Array}
     */
    transformData: function (chartType, data, firstRowIsHeader, columnTypes, colors) {
      // Validate data first
      const validation = this.validateData(data, chartType);
      if (!validation.valid) {
        console.warn('Data validation failed:', validation.error);
        return { labels: [], datasets: [] };
      }

      const startRow = firstRowIsHeader ? 1 : 0;
      let labels = [];
      let datasets = [];

      // Pie and Wordcloud charts
      if (chartType === 'pie' || chartType === 'wordcloud') {
        return this._transformPieWordcloud(data, startRow, chartType, colors);
      }

      // Bubble charts
      if (chartType === 'bubble') {
        return this._transformBubble(data, startRow, colors);
      }

      // All other chart types (bar, column, line, area, etc.)
      return this._transformStandard(data, startRow, firstRowIsHeader, columnTypes, colors, chartType);
    },

    /**
     * Transforms data for pie/wordcloud charts
     * @private
     */
    _transformPieWordcloud: function (data, startRow, chartType, colors) {
      const labels = [];
      const values = [];

      for (let i = startRow; i < data.length; i++) {
        if (data[i] && data[i][0]) {
          const label = String(data[i][0]).trim();
          if (label) {
            labels.push(label);
            const value = this.parseNumber(data[i][1], 0);
            values.push(value);
          }
        }
      }

      const dataset = { data: values };

      // Apply colors for pie charts
      if (chartType === 'pie') {
        const pieColors = labels.map((label, idx) => {
          return colors && colors[label]
            ? colors[label]
            : this.getDefaultColor(idx);
        });
        dataset.backgroundColor = pieColors;
      }

      return { labels, datasets: [dataset] };
    },

    /**
     * Transforms data for bubble charts
     * @private
     */
    _transformBubble: function (data, startRow, colors) {
      const numCols = data[0] ? data[0].length : 0;
      const datasets = [];

      // Each dataset is X, Y, Size (3 columns)
      for (let col = 1; col < numCols; col += 3) {
        const bubbleData = [];
        const xCol = col;
        const yCol = col + 1;
        const sizeCol = col + 2;

        for (let i = startRow; i < data.length; i++) {
          if (data[i] && data[i][0] && String(data[i][0]).trim()) {
            const x = this.parseNumber(data[i][xCol], 0);
            const y = yCol < numCols ? this.parseNumber(data[i][yCol], 0) : 0;
            const r = sizeCol < numCols ? this.parseNumber(data[i][sizeCol], 10) : 10;
            bubbleData.push({ x, y, r });
          }
        }

        if (bubbleData.length > 0) {
          const label = (yCol < numCols && data[0] && data[0][yCol])
            ? String(data[0][yCol])
            : `Dataset ${Math.floor(col / 3) + 1}`;

          const dataset = { label, data: bubbleData };

          // Apply color if available
          if (colors && colors[label]) {
            dataset.backgroundColor = colors[label];
          }

          datasets.push(dataset);
        }
      }

      return { datasets };
    },

    /**
     * Transforms data for standard charts (bar, column, line, area)
     * @private
     */
    _transformStandard: function (data, startRow, firstRowIsHeader, columnTypes, colors, chartType) {
      const labels = [];
      const datasets = [];

      // Process all rows to preserve empty rows and maintain alignment
      // First pass: extract labels for all rows
      for (let i = startRow; i < data.length; i++) {
        if (data[i] && data[i][0]) {
          const label = String(data[i][0]).trim();
          labels.push(label || ""); // Preserve empty rows with empty string
        } else {
          labels.push(""); // Preserve completely empty rows
        }
      }

      // Extract datasets - align data with labels by index
      const numCols = data[0] ? data[0].length : 0;
      for (let col = 1; col < numCols; col++) {
        const datasetData = [];

        for (let i = startRow; i < data.length; i++) {
          const rowLabel = labels[i - startRow];
          const hasLabel = rowLabel && rowLabel.trim() !== "";

          if (hasLabel) {
            // Row has label - check if it has data in this column
            if (data[i] && data[i][col] !== undefined && data[i][col] !== null && data[i][col] !== "") {
              const value = this.parseNumber(data[i][col], NaN);
              // Only push if it's a valid number (not NaN)
              if (!isNaN(value)) {
                datasetData.push(value);
              } else {
                // Invalid number - use NaN to skip rendering
                datasetData.push(NaN);
              }
            } else {
              // Row has label but no data in this column - use NaN to skip rendering
              datasetData.push(NaN);
            }
          } else {
            // Empty row - use NaN to skip rendering and preserve position
            datasetData.push(NaN);
          }
        }

        // Only create dataset if there's at least one valid number (not NaN)
        const hasData = datasetData.some(val => val !== null && val !== undefined && !isNaN(val));
        if (hasData) {
          const label = firstRowIsHeader && data[0]
            ? String(data[0][col])
            : `Dataset${col}`;

          const color = colors && colors[label]
            ? colors[label]
            : this.getDefaultColor(col - 1);

          const dataset = {
            label,
            data: datasetData,
            backgroundColor: color
          };

          // Set type for line-column combo
          if (columnTypes && columnTypes[col]) {
            // Convert "column" to "bar" as Chart.js doesn't recognize "column"
            const colType = columnTypes[col];
            dataset.type = colType === "column" ? "bar" : colType;
          }

          // Set border color for line/area charts
          if (chartType === 'line' || this.isArea(chartType)) {
            dataset.borderColor = color;
          }

          datasets.push(dataset);
        }
      }

      return { labels, datasets };
    },

    /**
     * Safely destroys a Chart.js instance
     * @param {Chart} chart - Chart instance
     */
    destroyChart: function (chart) {
      if (!chart) {
        return;
      }

      try {
        if (typeof chart.destroy === 'function') {
          chart.destroy();
        }
      } catch (e) {
        console.warn('Error destroying chart:', e);
      }
    },

    /**
     * Enhances canvas rendering quality
     * @param {HTMLCanvasElement} canvas - Canvas element
     */
    enhanceCanvasQuality: function (canvas) {
      if (!canvas) {
        return;
      }

      try {
        const ctx = canvas.getContext('2d', {
          alpha: true,
          desynchronized: false
        });
        if (ctx) {
          ctx.imageSmoothingEnabled = true;
          if (ctx.imageSmoothingQuality) {
            ctx.imageSmoothingQuality = 'high';
          }
        }
      } catch (e) {
        console.warn('Error enhancing canvas quality:', e);
      }
    },

    /**
     * Safely destroys a chart from a canvas element
     * @param {HTMLCanvasElement} canvas - Canvas element
     */
    destroyChartFromCanvas: function (canvas) {
      if (!canvas) {
        return;
      }

      try {
        // Try to get existing chart instance
        const existingChart = window.Chart && window.Chart.getChart ? window.Chart.getChart(canvas) : null;
        if (existingChart) {
          this.destroyChart(existingChart);
        }

        // Clear canvas
        const ctx = canvas.getContext('2d');
        if (ctx) {
          ctx.clearRect(0, 0, canvas.width, canvas.height);
        }
      } catch (e) {
        console.warn('Error destroying chart from canvas:', e);
      }
    },

    /**
     * Creates a debounced function
     * @param {Function} func - Function to debounce
     * @param {number} wait - Wait time in ms
     * @returns {Function} Debounced function
     */
    debounce: function (func, wait) {
      let timeout;
      return function executedFunction(...args) {
        const later = () => {
          clearTimeout(timeout);
          func.apply(this, args);
        };
        clearTimeout(timeout);
        timeout = setTimeout(later, wait);
      };
    },

    /**
     * Creates a throttled function
     * @param {Function} func - Function to throttle
     * @param {number} limit - Time limit in ms
     * @returns {Function} Throttled function
     */
    throttle: function (func, limit) {
      let inThrottle;
      return function executedFunction(...args) {
        if (!inThrottle) {
          func.apply(this, args);
          inThrottle = true;
          setTimeout(() => inThrottle = false, limit);
        }
      };
    }
  };

  // Export to window
  window.GraphUtils = GraphUtils;

})(window);
